/**
 * Types and constants for the Add / Edit Truck Master form.
 * Mirrors the backend TruckMasterDto (Maleva/module/fleet/dto/TruckMasterDto.java).
 */

export interface TruckMasterDto {
  id?: number | null;
  companyRefId: number;
  truckName: string;
  truckNumber: string;
  truckNumber1?: string | null;
  truckType: string;
  vehicleType?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  active: number;
  /** 1 = Maleva-owned truck, 0 = outside / subcontractor truck */
  malevaTruck: number;
  /** Pallet spaces this truck holds; null means "use the usual figure for its size". */
  palletCapacity?: number | null;
  /** ACTIVE, WORKSHOP or SOLD. Blank reads as ACTIVE. */
  truckStatus?: string | null;
  /** The day a WORKSHOP truck is due back; null means until further notice. */
  workshopUntil?: string | null;
  /** This truck's own WhatsApp group id ("1203...@g.us"); blank = no group. */
  whatsAppGroup?: string | null;
  /** Read-only: the driver who normally drives this truck (stored on the driver). */
  driverRefId?: number | null;
  driverName?: string | null;
  cNumber: number;
  cNumberDisplay: string;
  accountRefid: number;

  rotexMyExp?: string | null;
  rotexSGExp?: string | null;
  puspacomExp?: string | null;
  insuranceExp?: string | null;
  bonamExp?: string | null;
  apadExp?: string | null;

  rotexMyExp1?: string | null;
  rotexSGExp1?: string | null;
  puspacomExp1?: string | null;

  serviceExp?: string | null;
  alignmentExp?: string | null;
  greeceExp?: string | null;
  gearOilExp?: string | null;
  ptpStickerExp?: string | null;
  sidExp?: string | null;

  serviceLast?: string | null;
  alignmentLast?: string | null;
  greeceLast?: string | null;
  gearOilLast?: string | null;
  /** When the battery was last replaced. */
  batteryDate?: string | null;
  /** Who last touched the record; the API stamps SYSTEM when absent. */
  modifiedBy?: string | null;

  accountCode?: string | null;

  /** Service kilometres, kept as text - the workshop types them freehand. */
  lastServiceKM?: string | null;
  nextServiceKM?: string | null;
  nextodometerKm?: string | null;

  /** Truck & Driver Details sheet fields (db/truck-driver-details.sql). */
  capacity?: string | null;
  sealType?: string | null;
  bondNo?: string | null;
  bondExp?: string | null;
}

/** Every optional-date field on the form (checkbox + date picker pairs). */
export type TruckDateField =
  | 'rotexMyExp'
  | 'rotexSGExp'
  | 'puspacomExp'
  | 'insuranceExp'
  | 'bonamExp'
  | 'apadExp'
  | 'rotexMyExp1'
  | 'rotexSGExp1'
  | 'puspacomExp1'
  | 'serviceExp'
  | 'alignmentExp'
  | 'greeceExp'
  | 'gearOilExp'
  | 'ptpStickerExp'
  | 'sidExp'
  | 'serviceLast'
  | 'alignmentLast'
  | 'greeceLast'
  | 'gearOilLast'
  | 'batteryDate'
  | 'bondExp';

export const TRUCK_DATE_FIELDS: TruckDateField[] = [
  'rotexMyExp',
  'rotexSGExp',
  'puspacomExp',
  'insuranceExp',
  'bonamExp',
  'apadExp',
  'rotexMyExp1',
  'rotexSGExp1',
  'puspacomExp1',
  'serviceExp',
  'alignmentExp',
  'greeceExp',
  'gearOilExp',
  'ptpStickerExp',
  'sidExp',
  'serviceLast',
  'alignmentLast',
  'greeceLast',
  'gearOilLast',
  'batteryDate',
  'bondExp',
];

/** Same list the legacy AddTruck page offered (Common.js → NewVechicleType). */
export const VEHICLE_TYPE_OPTIONS = ['Maleva Vehicle', 'Personal Vehicle', 'Other Vehicle'];

export interface TruckFormState {
  truckName: string;
  truckNumber: string;
  truckNumber1: string;
  truckType: string;
  vehicleType: string;
  latitude: string;
  longitude: string;
  lastServiceKM: string;
  nextServiceKM: string;
  nextodometerKm: string;
  active: boolean;
  malevaTruck: 0 | 1;
  /** Pallet spaces, as typed. Empty means "use the usual figure for the size". */
  palletCapacity: string;
  /** ACTIVE, WORKSHOP or SOLD. */
  truckStatus: string;
  /** `YYYY-MM-DD`, only meaningful while the status is WORKSHOP. */
  workshopUntil: string;
  /** The truck's WhatsApp group id, as typed. Blank = this truck is not shared to a group. */
  whatsAppGroup: string;
  /** As printed on the Truck & Driver Details sheet, e.g. "40FT", "5-SEAL". */
  capacity: string;
  sealType: string;
  bondNo: string;
  /** Chosen driver id as text, '' for none. Saved separately from the truck. */
  driverRefId: string;
  dates: Record<TruckDateField, string>; // '' = not set (checkbox off)
}

export const emptyTruckForm = (): TruckFormState => ({
  truckName: '',
  truckNumber: '',
  truckNumber1: '',
  truckType: '',
  vehicleType: '',
  latitude: '',
  longitude: '',
  lastServiceKM: '',
  nextServiceKM: '',
  nextodometerKm: '',
  active: true,
  malevaTruck: 1,
  palletCapacity: '',
  truckStatus: 'ACTIVE',
  workshopUntil: '',
  whatsAppGroup: '',
  capacity: '',
  sealType: '',
  bondNo: '',
  driverRefId: '',
  dates: TRUCK_DATE_FIELDS.reduce(
    (acc, field) => ({ ...acc, [field]: '' }),
    {} as Record<TruckDateField, string>
  ),
});


/**
 * How long each servicing item lasts before it is due again.
 *
 * Setting the "last done" date is what the workshop actually records; the due
 * date follows from it by a fixed interval, so the form works it out rather
 * than asking someone to count days on a calendar and get it wrong. The
 * calculated date stays editable - a truck can be brought in early.
 */
export interface ServiceInterval {
  /** The date the workshop enters. */
  last: TruckDateField;
  /** The date derived from it. */
  next: TruckDateField;
  label: string;
  days?: number;
  months?: number;
  /** Shown next to the field so the rule is visible, not folded into code. */
  describe: string;
}

export const SERVICE_INTERVALS: ServiceInterval[] = [
  { last: 'serviceLast', next: 'serviceExp', label: 'Service', days: 40, describe: '+40 days' },
  { last: 'alignmentLast', next: 'alignmentExp', label: 'Alignment', days: 30, describe: '+30 days' },
  { last: 'greeceLast', next: 'greeceExp', label: 'Grease', days: 14, describe: '+14 days' },
  { last: 'gearOilLast', next: 'gearOilExp', label: 'Gear Oil', months: 6, describe: '+6 months' },
];

/** The rule that derives this field, if it is a derived one. */
export const intervalForNext = (field: TruckDateField): ServiceInterval | undefined =>
  SERVICE_INTERVALS.find((rule) => rule.next === field);

/**
 * Adds an interval to a yyyy-MM-dd date.
 *
 * Months are added by calendar, then clamped to the end of the target month:
 * plain setMonth turns 31 August + 6 months into 3 March, because February has
 * no 31st and JavaScript rolls the overflow forward. 28 February is the answer
 * a workshop expects.
 */
export const addServiceInterval = (isoDate: string, rule: ServiceInterval): string => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return '';

  const result = new Date(year, month - 1, day);
  if (rule.months) {
    const targetMonth = result.getMonth() + rule.months;
    const lastDayOfTarget = new Date(year, targetMonth + 1, 0).getDate();
    result.setMonth(targetMonth, Math.min(day, lastDayOfTarget));
  }
  if (rule.days) {
    result.setDate(result.getDate() + rule.days);
  }
  if (Number.isNaN(result.getTime())) return '';

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${result.getFullYear()}-${pad(result.getMonth() + 1)}-${pad(result.getDate())}`;
};

/** Convert any backend date value to an <input type="date"> value ('' when absent). */
export const toInputDate = (value: unknown): string => {
  if (!value) return '';
  const str = String(value);
  // Already YYYY-MM-DD (possibly with time part)
  const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * Whether a truck is on the road, being repaired, or gone. Mirrors the server's
 * TruckStatus enum - keep the two in step.
 *
 * A WORKSHOP truck is not offered on the Truck Order Calendar until its return
 * date has passed; SOLD is never offered again. Neither deletes anything: the
 * truck keeps its history either way.
 */
export const TRUCK_STATUSES = [
  { code: 'ACTIVE', label: 'Active - on the road' },
  { code: 'WORKSHOP', label: 'In workshop - not bookable' },
  { code: 'SOLD', label: 'Sold - never bookable' },
] as const;

export type TruckStatusCode = (typeof TRUCK_STATUSES)[number]['code'];
